import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Mail, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.message.trim() || form.message.trim().length < 5) {
      toast.error('Please add your email and a real message.');
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      toast.success("Thanks — we'll be in touch within 24 hours.");
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="container mx-auto px-4 md:px-8 py-12 max-w-2xl" data-testid="contact-page">
      <Link to="/" className="inline-flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground hover:text-foreground mb-8">
        <ArrowLeft className="w-4 h-4" /> Back
      </Link>

      <div className="flex items-center gap-3 mb-2">
        <Mail className="w-6 h-6 text-primary" />
        <h1 className="font-heading text-4xl">CONTACT US</h1>
      </div>
      <p className="text-xs uppercase tracking-widest text-muted-foreground mb-6">
        Real humans. Responses within 24 hours.
      </p>
      <p className="text-muted-foreground leading-relaxed mb-8">
        Questions about your Life Profile, the browser extension, billing or your privacy? Send us a message below or email
        {' '}<a href="mailto:support@lifeautofill.com" className="text-primary hover:underline" data-testid="contact-email-link">support@lifeautofill.com</a>.
        A lot of common questions are already answered in the <Link to="/help" className="text-primary hover:underline">Help Center</Link>.
      </p>

      <div className="grid sm:grid-cols-3 gap-3 mb-10" data-testid="contact-info">
        {[
          ['SUPPORT', 'Extension issues, missed fields, parsing errors. Tell us the website and paste any [LifeAdmin] console logs.'],
          ['BILLING', 'Refunds, invoices and plan changes. You can also manage everything in Settings → Subscription.'],
          ['PRIVACY', 'Requests to access, correct or delete your data are answered within 30 days.'],
        ].map(([t, d]) => (
          <div key={t} className="border-2 border-border p-4">
            <div className="font-heading text-xs uppercase tracking-widest text-primary mb-2">{t}</div>
            <p className="text-xs text-muted-foreground leading-relaxed">{d}</p>
          </div>
        ))}
      </div>

      {sent ? (
        <div className="border border-accent p-8 text-center" data-testid="contact-success">
          <h2 className="font-heading text-xl mb-2">MESSAGE RECEIVED</h2>
          <p className="text-sm text-muted-foreground mb-6">
            Thanks for reaching out. We&apos;ll get back to you within 24 hours.
          </p>
          <Link to="/" className="inline-block px-5 py-2 border border-primary text-primary uppercase text-xs tracking-widest hover:bg-primary hover:text-primary-foreground transition-colors">
            BACK TO HOME
          </Link>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2 block">YOUR NAME</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Full name"
                data-testid="contact-name"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2 block">EMAIL *</Label>
              <Input
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="you@example.com"
                data-testid="contact-email"
              />
            </div>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2 block">SUBJECT</Label>
            <Input
              value={form.subject}
              onChange={(e) => setForm({ ...form, subject: e.target.value })}
              placeholder="What's this about?"
              data-testid="contact-subject"
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2 block">MESSAGE *</Label>
            <textarea
              required
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              placeholder="Tell us what's going on..."
              rows={7}
              className="w-full border border-border bg-transparent px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-ring resize-y"
              data-testid="contact-message"
            />
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground mt-2">
              {form.message.length}/5000 characters
            </p>
          </div>
          <Button
            type="submit"
            disabled={sending}
            className="w-full brutalist-shadow uppercase tracking-widest text-xs py-6"
            data-testid="contact-submit"
          >
            {sending ? 'SENDING...' : (<><Send className="w-4 h-4 mr-2" /> SEND MESSAGE</>)}
          </Button>
        </form>
      )}

      <p className="text-[10px] uppercase tracking-widest text-muted-foreground mt-10" data-testid="contact-business-details">
        Life Admin Autofill · ABN 74 647 107 845 · Australia · Support hours Mon–Fri, 9am–5pm AEST
      </p>
    </div>
  );
}
